import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const TONE_COLOR = {
    default: 'var(--text)',
    accent: 'var(--amber)',
    success: 'var(--success)',
    warning: 'var(--warning)',
    danger: 'var(--danger)',
    steel: 'var(--steel)',
};
export function Button({ children, onClick, tone = 'default', variant = 'solid', block = false, disabled = false, type = 'button', minHeight = 44, style, 'aria-label': ariaLabel, }) {
    const bg = variant === 'solid'
        ? tone === 'accent'
            ? 'var(--amber)'
            : tone === 'default'
                ? 'var(--ink-700)'
                : 'transparent'
        : 'transparent';
    const color = variant === 'solid' && tone === 'accent' ? '#0b0f14' : TONE_COLOR[tone];
    const border = variant === 'solid' && tone !== 'accent'
        ? tone === 'default'
            ? '1px solid var(--line)'
            : '1px solid transparent'
        : `1px solid ${TONE_COLOR[tone]}`;
    return (_jsx("button", { type: type, onClick: onClick, disabled: disabled, "aria-label": ariaLabel, "aria-disabled": disabled, style: {
            minHeight,
            width: block ? '100%' : undefined,
            background: bg,
            color,
            border,
            borderRadius: 'var(--r-button)',
            padding: '0 16px',
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 15,
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.5 : 1,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 8,
            ...style,
        }, children: children }));
}
export function Chip({ children, active = false, onClick, tone = 'accent' }) {
    const color = active ? 'var(--amber)' : 'var(--muted)';
    return (_jsx("button", { type: "button", onClick: onClick, "aria-pressed": active, style: {
            minHeight: 32,
            padding: '0 12px',
            borderRadius: 'var(--r-chip)',
            border: `1px solid ${active ? 'var(--amber)' : 'var(--line)'}`,
            background: active ? 'rgba(255,122,26,.12)' : 'transparent',
            color,
            fontFamily: 'var(--font-sans)',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
        }, children: children }));
}
export function Card({ children, title, action, style, }) {
    return (_jsxs("section", { style: {
            background: 'var(--ink-800)',
            border: '1px solid var(--line)',
            borderRadius: 'var(--r-card)',
            padding: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
            ...style,
        }, children: [(title || action) && (_jsxs("header", { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }, children: [title ? (_jsx("h3", { style: { margin: 0, fontSize: 15, fontWeight: 700, fontFamily: 'var(--font-sans)' }, children: title })) : (_jsx("span", {})), action] })), children] }));
}
export function Badge({ children, tone = 'accent', }) {
    return (_jsx("span", { style: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            padding: '2px 10px',
            borderRadius: 'var(--r-pill)',
            border: `1px solid ${TONE_COLOR[tone]}`,
            color: TONE_COLOR[tone],
            fontSize: 12,
            fontWeight: 700,
            fontFamily: 'var(--font-mono)',
            whiteSpace: 'nowrap',
        }, children: children }));
}
export function Field({ label, value, onChange, type = 'text', placeholder, required = false, error, inputMode, autoComplete, disabled = false, }) {
    return (_jsxs("label", { style: { display: 'flex', flexDirection: 'column', gap: 6 }, children: [_jsxs("span", { style: {
                    fontSize: 12,
                    color: 'var(--muted)',
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '.06em',
                    textTransform: 'uppercase',
                }, children: [label, required ? ' *' : ''] }), _jsx("input", { value: value, type: type, placeholder: placeholder, inputMode: inputMode, autoComplete: autoComplete, required: required, disabled: disabled, onChange: (e) => onChange(e.target.value), style: {
                    minHeight: 44,
                    padding: '0 12px',
                    borderRadius: 'var(--r-field)',
                    border: `1px solid ${error ? 'var(--danger)' : 'var(--line)'}`,
                    background: 'var(--ink-900)',
                    color: 'var(--text)',
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                    width: '100%',
                    opacity: disabled ? 0.6 : 1,
                } }), error ? (_jsx("span", { style: { color: 'var(--danger)', fontSize: 12, fontFamily: 'var(--font-sans)' }, children: error })) : null] }));
}
/**
 * Экран Mini App: шапка с контекстом и нижняя навигация (ТЗ FR-DASH-05):
 * главная, каталог, мои билеты, квест, профиль.
 */
export function Screen({ title, subtitle, children, bottomNav, headerRight, headerLeft, }) {
    return (_jsxs("div", { style: {
            minHeight: '100dvh',
            background: 'var(--ink-900)',
            color: 'var(--text)',
            fontFamily: 'var(--font-sans)',
            display: 'flex',
            flexDirection: 'column',
        }, children: [title && (_jsxs("header", { style: {
                    padding: '16px 16px 8px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 12,
                }, children: [headerLeft ? _jsx("div", { style: { flex: '0 0 auto' }, children: headerLeft }) : null, _jsxs("div", { style: { flex: 1, minWidth: 0 }, children: [_jsx("h1", { style: { margin: 0, fontSize: 20, fontWeight: 800, lineHeight: 1.2 }, children: title }), subtitle ? (_jsx("p", { style: { margin: '4px 0 0', fontSize: 13, color: 'var(--muted)' }, children: subtitle })) : null] }), headerRight] })), _jsx("main", { style: { flex: 1, padding: '12px 16px 24px', display: 'flex', flexDirection: 'column', gap: 16 }, children: children }), bottomNav] }));
}
/** Нижняя навигация: 5 пунктов, зона нажатия не менее 44 px. */
export function BottomNav({ items, onSelect }) {
    return (_jsx("nav", { style: {
            display: 'grid',
            gridTemplateColumns: `repeat(${items.length}, 1fr)`,
            borderTop: '1px solid var(--line)',
            background: 'var(--ink-850)',
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
        }, children: items.map((it) => (_jsxs("button", { type: "button", onClick: () => onSelect?.(it.key), "aria-current": it.active ? 'page' : undefined, style: {
                minHeight: 56,
                border: 'none',
                background: 'transparent',
                color: it.active ? 'var(--amber)' : 'var(--muted)',
                fontSize: 11,
                fontFamily: 'var(--font-sans)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 2,
                cursor: 'pointer',
            }, children: [it.icon, _jsx("span", { children: it.label })] }, it.key))) }));
}
/**
 * Единый блок состояния — все состояния интерфейса проработаны (ТЗ §8.1).
 * Информация не кодируется только цветом: статус сопровождается текстом и знаком.
 */
export function StateBlock({ kind, title, description, actionLabel, onAction, }) {
    const sign = {
        loading: '⋯',
        empty: '○',
        error: '!',
        offline: '✕',
        forbidden: '⊘',
        unconfirmed: '?',
    };
    const color = {
        loading: 'var(--muted)',
        empty: 'var(--muted)',
        error: 'var(--danger)',
        offline: 'var(--warning)',
        forbidden: 'var(--warning)',
        unconfirmed: 'var(--steel)',
    };
    return (_jsxs("div", { role: "status", style: {
            padding: 24,
            borderRadius: 'var(--r-card)',
            border: '1px dashed var(--line)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 8,
            textAlign: 'center',
        }, children: [_jsx("span", { "aria-hidden": true, style: { color: color[kind], fontSize: 22, fontFamily: 'var(--font-mono)' }, children: sign[kind] }), _jsx("strong", { style: { fontSize: 15 }, children: title }), description ? (_jsx("p", { style: { margin: 0, color: 'var(--muted)', fontSize: 13, maxWidth: 320 }, children: description })) : null, actionLabel && onAction ? (_jsx(Button, { tone: "accent", variant: "outline", onClick: onAction, style: { marginTop: 8 }, children: actionLabel })) : null] }));
}
/** Числовой показатель — моноширинный шрифт для цифр, кодов и слотов (ТЗ §8.2). */
export function Stat({ label, value, hint }) {
    return (_jsxs("div", { style: { display: 'flex', flexDirection: 'column', gap: 2 }, children: [_jsx("span", { style: { fontSize: 12, color: 'var(--muted)', fontFamily: 'var(--font-mono)' }, children: label }), _jsx("strong", { style: { fontSize: 18, fontFamily: 'var(--font-mono)' }, children: value }), hint ? _jsx("span", { style: { fontSize: 12, color: 'var(--muted-2)' }, children: hint }) : null] }));
}
export function Skeleton({ height = 16, width = '100%' }) {
    return (_jsx("div", { "aria-hidden": true, style: {
            height,
            width,
            borderRadius: 'var(--r-field)',
            background: 'var(--ink-700)',
            opacity: 0.6,
        } }));
}
/** Денежное значение — копейки → человекочитаемый вид (ТЗ §7.3: minor units). */
export function formatMoney(minor, currency = 'RUB') {
    const value = minor / 100;
    return `${value.toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} ₽ ${currency === 'RUB' ? '' : currency}`.trim();
}
/** Дата и время — ISO 8601 → локальное представление пользователя (ТЗ §4.5). */
export function formatDateTime(iso, locale = 'ru-RU') {
    const d = new Date(iso);
    return d.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short' });
}
export function formatDate(iso, locale = 'ru-RU') {
    return new Date(iso).toLocaleDateString(locale, { dateStyle: 'medium' });
}
//# sourceMappingURL=components.js.map